/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Unterhalt_700px.jpg";
import Typography from "@mui/material/Typography";
import { Box, Link as MuiLink, Paper, TableHead } from "@mui/material";
import MuiListNativeStyled from "../../List/MuiListNativeStyled";
import TableBody from "@mui/material/TableBody";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { gaSet } from "../../../services/googleAnalyticsService";
import { Link } from "react-router";

const GuidebookDivorceAlimony = () => {
  const title = "Ratgeber - Unterhalt | Legalbird";

  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content="Wir erklären Ihnen, welche Arten von Unterhalt es gibt und wie diese berechnet werden." />
      </Helmet>

      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan slogan={"Alles zum Thema Unterhalt"} backgroundImagePath={backgroundTeaser} height={300} headingColor={"#333333"} />
        <TextContainer>
          <p>
            Wenn Sie sich scheiden lassen möchten, kommt schnell die Frage nach den damit verbundenen Kosten auf. Entscheidend sind hierbei die
            Unterhaltszahlungen, die nach einer Trennung auf sie zukommen können. Wir erklären Ihnen, welche Arten von Unterhalt es gibt und wie diese berechnet
            werden.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Drei Arten von Unterhaltszahlungen
          </Typography>
          <p>Je nachdem wie weit Ihre Trennung fortgeschritten ist, gibt es drei Arten von Unterhaltszahlungen:</p>
          <MuiListNativeStyled
            sx={{ listStyle: "decimal" }}
            listItems={[
              <>
                <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#trennungsunterhalt"}>
                  Trennungsunterhalt
                </MuiLink>{" "}
                - gilt ab dem Zeitpunkt der Trennung bis zur Scheidung
              </>,
              <>
                <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#nachehelicher-unterhalt"}>
                  Nachehelicher Unterhalt
                </MuiLink>{" "}
                - gilt ab der Scheidung
              </>,
              <>
                <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#kindesunterhalt"}>
                  Kindesunterhalt
                </MuiLink>{" "}
                - bekommt ab dem getrennten Wohnen der Ehepartner, bei dem das Kind nicht wohnt
              </>,
            ]}
          />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"trennungsunterhalt"} />
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            1. Was ist der Trennungsunterhalt?
          </Typography>
          <p>
            Um sicher zu stellen, dass beide Ehepartner nach einer Trennung finanziell abgesichert sind, gibt es bereits ab dem Zeitpunkt einer Trennung den
            sogenannten Trennungsunterhalt. Bei dieser Art des Unterhalts handelt es sich um einen Ausgleich des Einkommens zugunsten des wirtschaftlich
            schlechter gestellten Ehepartners während des Getrenntlebens bis zur Scheidung.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Voraussetzungen für den Trennungsunterhalt?
          </Typography>
          <ol>
            <li>die Ehepartner leben getrennt</li>
            <li>die Bedürftigkeit des unterhaltbegehrenden Ehegatten</li>
            <li>ein Ehepartner ist finanziell schlechter gestellt und daher auf die Unterstützung des anderen angewiesen</li>
            <li>der schlechter gestellte Ehepartner ist selbst nicht leistungsfähig/berufsfähig</li>
            <li>der höher bzw. alleine verdienende Ehegatte muss leistungsfähig sein, sprich ihm steht ein Selbstbehalt in Höhe von 1.750 € monatlich zu</li>
          </ol>
          <p>
            Zur Leistungsfähigkeit/Berufstätigkeit:
            <br />
            Wenn der geringer verdienende Ehepartner während der Ehe nicht berufstätig war, ist er auch während des Trennungsjahres nicht verpflichtet zu
            arbeiten. Wenn dieser Ehepartner zudem das Kind betreut, gelten weitere Einschränkungen. Allgemein gilt: Regelmäßig muss der Ehepartner in den
            ersten drei Lebensjahren des Kindes nicht arbeiten gehen.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Wie berechnet sich der Trennungsunterhalt?
          </Typography>
          <p>
            Basis der Berechnung ist der Lebensstandard und somit die Einkünfte während des Zeitraums der Ehe. Es werden daher zunächst die Einkommen der
            Ehegatten miteinander verglichen. Wenn ein Ehepartner weniger verdient, muss der andere Ehegatte einen Teil seines Einkommens abgeben, um die
            Differenz auszugleichen. Wenn sich die Ehepartner nicht basierend auf den gesetzlichen Vorgaben über den Unterhalt einigen können, entscheidet auf
            Antrag eines Ehegatten das Gericht.
            <br />
            Ausgangspunkt für die Berechnung ist das Nettoeinkommen (also nach Steuerzahlungen). Von diesem Nettoeinkommen werden Verpflichtungen wie zum
            Beispiel Kreditraten und Versicherungen abgezogen. Nach diesen Abzügen ergibt sich das bereinigte Nettoeinkommen. Arbeitende Ehepartner erhalten
            einen Erwerbstätigenbonus - von dem bereinigten Nettoeinkommen werden 10% abgezogen. Dieser Bonus soll den erwerbstätigen Ehepartner darin
            bekräftigen, auch weiter arbeiten zu gehen.
            <br />
            Der Unterhaltspflichtige Ehegatte hat an den Berechtigten <strong>45% seines Nettoeinkommens bzw. 45% des Differenzeinkommens</strong> (wenn beide
            über Erwerbseinkommen verfügen) <strong>als Trennungsunterhalt</strong> zu zahlen.
          </p>
          <HighlightBox headline={"Rechenbeispiel Trennungsunterhalt"}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues} scope={"col"}>
                    Position
                  </TableCell>
                  <TableCell sx={{ ...guidebookContentStyle.tableValues, ...guidebookContentStyle.tableValuesRight }} scope={"col"}>
                    Betrag
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Bereinigtes Nettoeinkommen Ehemann</TableCell>
                  <TableCell sx={{ ...guidebookContentStyle.tableValues, ...guidebookContentStyle.tableValuesRight }}>3.500 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Bereinigtes Nettoeinkommen Ehefrau</TableCell>
                  <TableCell sx={{ ...guidebookContentStyle.tableValues, ...guidebookContentStyle.tableValuesRight }}>1.400 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Einkommensdifferenz (3.500 € - 1.400 €)</TableCell>
                  <TableCell sx={{ ...guidebookContentStyle.tableValues, ...guidebookContentStyle.tableValuesRight }}>2.100 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>45% Unterhaltsanspruch der Ehefrau</TableCell>
                  <TableCell sx={{ ...guidebookContentStyle.tableValues, ...guidebookContentStyle.tableValuesRight }}>945 €</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <p>In diesem Beispiel müsste also der Ehemann bis zur Scheidung 945 € monatlichen Trennungsunterhalt an die Ehefrau zahlen.</p>
          </HighlightBox>
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"nachehelicher-unterhalt"} />
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            2. Was ist der nacheheliche Unterhalt?
          </Typography>
          <p>
            Der nacheheliche Unterhalt, auch Ehegattenunterhalt genannt, gilt ab der rechtskräftigen Scheidung. Allgemein gilt: Jeder Ehepartner ist nach der
            Scheidung dazu verpflichtet, sich seinen Unterhalt selbst zu verdienen.
          </p>
          <p>
            Wenn ein Ehepartner nach der Scheidung nicht in der Lage ist, sich selbst zu versorgen, hat er einen Unterhaltsanspruch. Dass er nicht in der Lage
            ist, sich durch eigenes Einkommen selbst zu versorgen, muss er dem anderen Ehepartner nachweisen (z.B. wegen Kindesbetreuung, Alter, Krankheit,
            etc.). Die Höhe des Unterhalts richtet sich nach den Lebensverhältnissen während der Ehe.
          </p>
          <p>
            Es besteht generell nur dann ein Unterhaltsanspruch, wenn der unterhaltspflichtige Ehepartner selbst leistungsfähig ist - er muss also nur Unterhalt
            zahlen, wenn er sich dies leisten kann und dadurch nicht selbst zu sehr eingeschränkt wird.
          </p>
          <p>
            Wie lange Unterhalt gezahlt werden muss, gibt das Gesetz nicht vor. Jedoch muss allgemein nicht mehr gezahlt werden, sobald der Grund für die
            Unterhaltszahlung entfällt, beispielsweise wenn der Zahlungsempfangende einer Arbeit nachgeht, die ein ausreichendes Einkommen einbringt.
          </p>
          <p>
            Wie viel Ehegattenunterhalt gezahlt werden muss, ist ähnlich zu beurteilen wie beim Trennungsunterhalt, aber je nach Fall und den Verhältnissen der
            Ehepartner sehr unterschiedlich. Eine pauschale Berechnung ist daher schwierig. Es gibt jedoch grundsätzliche Vorgaben für die Berechnung, die als
            Orientierung dienen können.
          </p>
          <p>
            Es gibt Fälle, bei denen kein oder nur verkürzt Unterhalt gezahlt werden muss. Zum Beispiel, wenn die Ehe nur kurz gedauert hat (als Orientierung:
            zwei Jahre werden als kurz angesehen), oder wenn der Unterhaltsberechtigte bereits in einer neuen verfestigten Lebensgemeinschaft lebt.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Wie berechnet sich der nacheheliche Unterhalt?
          </Typography>
          <p>
            Beim nachehelichen Unterhalt gilt die gleiche Berechnung wie beim Trennungsunterhalt, d.h. Ausgangspunkt für die Berechnung ist das Nettoeinkommen
            (also nach Steuerzahlungen). Von diesem Nettoeinkommen werden Verpflichtungen wie zum Beispiel Kreditraten und Versicherungen abgezogen. Nach diesen
            Abzügen ergibt sich das bereinigte Nettoeinkommen. Arbeitende Ehepartner erhalten einen Erwerbstätigenbonus - von dem bereinigten Nettoeinkommen
            werden 10% abgezogen. Dieser Bonus soll den erwerbstätigen Ehepartner darin bekräftigen, auch weiter arbeiten zu gehen. Der Unterhaltspflichtige
            Ehegatte hat an den Berechtigten <strong>45% seines Nettoeinkommens bzw. 45% des Differenzeinkommens</strong> (wenn beide über Erwerbseinkommen
            verfügen) <strong>als Trennungsunterhalt</strong> zu zahlen.
          </p>
          <HighlightBox headline={"Rechenbeispiel nachehelicher Unterhalt"}>
            <p>Gleiche Rechnung wie beim Trennungsunterhalt:</p>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues} scope={"col"}>
                    Position
                  </TableCell>
                  <TableCell sx={{ ...guidebookContentStyle.tableValues, ...guidebookContentStyle.tableValuesRight }} scope={"col"}>
                    Betrag
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Bereinigtes Nettoeinkommen Ehemann</TableCell>
                  <TableCell sx={{ ...guidebookContentStyle.tableValues, ...guidebookContentStyle.tableValuesRight }}>3.500 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Bereinigtes Nettoeinkommen Ehefrau</TableCell>
                  <TableCell sx={{ ...guidebookContentStyle.tableValues, ...guidebookContentStyle.tableValuesRight }}>1.400 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>Einkommensdifferenz (3.500 € - 1.400 €)</TableCell>
                  <TableCell sx={{ ...guidebookContentStyle.tableValues, ...guidebookContentStyle.tableValuesRight }}>2.100 €</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={guidebookContentStyle.tableValues}>45% Unterhaltsanspruch der Ehefrau</TableCell>
                  <TableCell sx={{ ...guidebookContentStyle.tableValues, ...guidebookContentStyle.tableValuesRight }}>945 €</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <p>In diesem Beispiel müsste also der Ehemann bis zur Scheidung 945 € nachehelichen Unterhalt an die Ehefrau zahlen.</p>
          </HighlightBox>
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"kindesunterhalt"} />
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            3. Was ist der Kindesunterhalt?
          </Typography>
          <p>
            Grundsätzlich hat jedes Kind Anspruch auf Unterhalt durch seine Eltern. Während die Eltern zusammenleben, wird dieser Unterhalt gleichverteilt durch
            Vater und Mutter durch die Erziehung, Unterkunft und Verpflegung des Kindes geleistet. Bei einer Trennung oder Scheidung müssen nach wie vor beide
            Elternteile Unterhalt für das Kind leisten.
            <br />
            Wie der Kindesunterhalt ab dem Getrenntleben der Eltern gesetzlich geregelt ist, haben wir hier für Sie zusammengefasst:
          </p>
          <p>
            Für minderjährige Kinder oder volljährige Kinder in Ausbildung oder Studium (wenn es die Erstausbildung ist) muss Unterhalt gezahlt werden. Der
            Elternteil, bei dem das Kind lebt, leistet den Unterhalt durch Unterkunft und Verpflegung (Naturalunterhalt), so dass der andere Elternteil in der
            Regel monatlich durch Geldzahlungen den Unterhalt des Kindes sicher stellen muss (Barunterhalt).
          </p>
          <p>
            Der Unterhalt muss monatlich im Voraus gezahlt werden. Wenn der Elternteil, bei dem das Kind nicht lebt, nicht in der Lage ist, Unterhalt zu zahlen,
            besteht unter bestimmten Voraussetzungen ein Anspruch auf Unterhaltsvorschuss durch den Staat.
          </p>
          <p>
            Wie viel Kindesunterhalt gezahlt werden muss, wird meist nach der sogenannten "Düsseldorfer Tabelle" berechnet, die - abhängig vom aktuellen
            Einkommen des Unterhaltszahlers und vom Alter des Kindes - die Höhe des Unterhalts vorgibt.
          </p>
          <p>
            Damit Eltern durch den Kindesunterhalt finanziell nicht zu sehr beeinträchtig werden, gibt es den sogenannten Selbstbehalt, also einen Anteil am
            Einkommen, den der Unterhaltszahlende immer behalten darf - dieser Anteil ist gesetzlich wie folgt definiert:
          </p>
          <ul>
            <li>Kind bis 21 Jahre (im Haushalt eines Elternteils lebend), Unterhaltszahlender arbeitet: Selbstbehalt 1.450 €</li>
            <li>Kind bis 21 Jahre (im Haushalt eines Elternteils lebend), Unterhaltszahlender arbeitet nicht: Selbstbehalt 1.200 €</li>
          </ul>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Wie berechnet sich der Kindesunterhalt?
          </Typography>
          <p>
            Die Höhe des Kindesunterhalts ist gesetzlich durch die Düsseldorfer Tabelle festgelegt. In dieser Tabelle wird, abhängig von dem Einkommen des
            Unterhaltszahlenden und des Alters des Kindes, ein genauer Mindestbetrag für den Unterhalt angegeben.
            <br />
            <strong>Wichtig:</strong> Dieser Betrag reduziert sich noch um das Kindergeld - bei minderjährigen Kindern wird das Kindergeld zur Hälfte abgezogen,
            bei volljährigen Kindern das vollständige Kindergeld. Bei näheren Fragen zu der aktuellen Düsseldorfer Tabelle helfen wir sehr gerne weiter!
          </p>
          <DoubleButton
            buttonText={"zum Scheidungsantrag"}
            buttonLink={"/servicewelt#my-cases"}
            backText={"zum Ratgeber"}
            backLink={"/ratgeber/familienrecht"}
          />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookDivorceAlimony;
